import type { CanonicalUserRecord } from '@responses/users';

export interface UserCapabilitiesManager {
  canBeEdited: boolean;
  /**
   * Whether the user could be resent an invite. E.g. if users is in the state that she could be re-invited to sign in.
   */
  canBeResentInvite: boolean;

  /**
   * Whether the user could be removed. E.g. if users is in the state that she could be removed from the system.
   */
  canBeRemoved: boolean;

  /**
   * Whether the user is in Pending state and could be updated as Pended.
   */
  canBeUpdatedAsPended: boolean;

  /**
   * Whether the user could be enabled or disabled in context of Cognito user.
   */
  canBeEnabledOrDisabled: boolean;

  userLifeCycleStatus: UserLifeCycleStatus;
}

export type CanonicalUserRecordWithCapabilities = CanonicalUserRecord & {
  capabilitiesManager: UserCapabilitiesManager;
};

/**
 * The Cognito `UserStatus` values the app checks for, a subset of the AWS SDK's `UserStatusType`.
 * It is declared here because the SPA loads this module and should not bundle the SDK.
 */
export enum CognitoUserStatus {
  Confirmed = 'CONFIRMED',
  ExternalProvider = 'EXTERNAL_PROVIDER',
  ForceChangePassword = 'FORCE_CHANGE_PASSWORD',
  ResetRequired = 'RESET_REQUIRED',
}

/**
 * User life cycle statuses
 *
 * @description
 *  The status of user within context of the Contract builder application.
 *  Please do not confuse with Cognito user statuses. As Cognito record does not cover entire user life cycle.
 */
export enum UserLifeCycleStatus {
  /**
   * User is active and has access to the application.
   */
  Active = 'Active',
  /**
   * User is pending (e.g. has not yet accessed the application, or has not yet completed the onboarding).
   */
  Pending = 'Pending',
  /**
   * User is disabled and has no access to the application.
   */
  Disabled = 'Disabled',

  /**
   * User is pending due to requirement to change password.
   */
  ResetRequired = 'ResetRequired',
}
